import { Extension, type Range } from '@tiptap/core';
import { Placeholder } from '@tiptap/extension-placeholder';
import { TextAlign } from '@tiptap/extension-text-align';
import {
  type Editor,
  EditorContent,
  posToDOMRect,
  useEditor,
  useEditorState
} from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import createSuggestion from '@tiptap/suggestion';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold as BoldIcon,
  Code as CodeIcon,
  Italic as ItalicIcon,
  List,
  ListOrdered,
  MessageSquareQuote,
  Strikethrough
} from 'lucide-react';
import {
  type ComponentProps,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import SlashCommandList, {
  type SlashCommandListHandle,
  type SlashDropdownValue
} from '@/components/SlashCommandList';
import SlashToken from '@/components/SlashToken';
import { InputGroup } from '@/components/ui/input-group';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Toggle } from '@/components/ui/toggle';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';

interface Measurable {
  getBoundingClientRect: () => DOMRect;
}

interface Props {
  defaultValue?: string;
  dropdownValues?: SlashDropdownValue[];
  id?: string;
  ref?: RefObject<Editor | null>;
}

interface SlashMenuState {
  items: SlashDropdownValue[];
  open: boolean;
  range: null | Range;
}

const RichTextField = ({
  defaultValue,
  dropdownValues = [],
  id,
  ref
}: Props) => {
  const dropdownValuesRef = useRef(dropdownValues);
  const slashListRef = useRef<SlashCommandListHandle>(null);
  const editorRef = useRef<Editor | null>(null);

  const [slashMenu, setSlashMenu] = useState<SlashMenuState>({
    items: [],
    open: false,
    range: null
  });

  // Virtual ref for Popover anchor - points to cursor position
  const virtualAnchorRef = useRef<Measurable | null>(null);

  useEffect(() => {
    dropdownValuesRef.current = dropdownValues;
  }, [dropdownValues]);

  const handleSlashCommand = useCallback(
    (item: SlashDropdownValue) => {
      const editor = editorRef.current;
      if (!editor || !slashMenu.range) return;

      editor
        .chain()
        .focus()
        .deleteRange(slashMenu.range)
        .insertContent([
          {
            attrs: { label: item.label, value: item.value },
            type: 'slashToken'
          },
          { text: ' ', type: 'text' }
        ])
        .run();

      setSlashMenu(prev => ({ ...prev, open: false, range: null }));
    },
    [slashMenu.range]
  );

  const handleSlashMenuOpenChange = useCallback((open: boolean) => {
    setSlashMenu(prev => ({ ...prev, open }));
  }, []);

  const slashCommandExtension = useMemo(
    () =>
      Extension.create({
        addProseMirrorPlugins() {
          return [
            createSuggestion<SlashDropdownValue, SlashDropdownValue>({
              allow: () => dropdownValuesRef.current.length > 0,
              char: '/',
              command: ({
                props,
                range
              }: {
                editor: Editor;
                props: SlashDropdownValue;
                range: Range;
              }) => {
                const editor = editorRef.current;
                if (!editor) return;

                editor
                  .chain()
                  .focus()
                  .deleteRange(range)
                  .insertContent([
                    {
                      attrs: { label: props.label, value: props.value },
                      type: 'slashToken'
                    },
                    { text: ' ', type: 'text' }
                  ])
                  .run();

                setSlashMenu(prev => ({ ...prev, open: false, range: null }));
              },
              editor: this.editor,
              items: ({ query }) => {
                const items = dropdownValuesRef.current;
                if (!items.length) return [];

                const q = query.trim().toLowerCase();
                if (q === '') return items.slice(0, 50);

                return items
                  .filter(item => {
                    const label = item.label.toLowerCase();
                    const value = item.value.toLowerCase();
                    return label.includes(q) || value.includes(q);
                  })
                  .slice(0, 50);
              },
              render: () => {
                return {
                  onExit() {
                    setSlashMenu(prev => ({
                      ...prev,
                      items: [],
                      open: false,
                      range: null
                    }));
                  },
                  onKeyDown(props) {
                    return slashListRef.current?.onKeyDown(props) ?? false;
                  },
                  onStart: props => {
                    editorRef.current = props.editor;

                    // Create virtual element for anchor positioning
                    const rect = posToDOMRect(
                      props.editor.view,
                      props.range.from,
                      props.range.to
                    );

                    virtualAnchorRef.current = {
                      getBoundingClientRect: () => rect
                    };

                    setSlashMenu({
                      items: props.items,
                      open: true,
                      range: props.range
                    });
                  },
                  onUpdate(props) {
                    editorRef.current = props.editor;

                    // Update virtual element position
                    const rect = posToDOMRect(
                      props.editor.view,
                      props.range.from,
                      props.range.to
                    );

                    virtualAnchorRef.current = {
                      getBoundingClientRect: () => rect
                    };

                    setSlashMenu({
                      items: props.items,
                      open: true,
                      range: props.range
                    });
                  }
                };
              }
            })
          ];
        },
        name: 'slashCommand'
      }),
    []
  );

  const editor = useEditor({
    content: defaultValue,
    editorProps: {
      attributes: {
        class:
          'rich-text-editor min-h-[96px] w-full px-4 pb-2 pt-3 text-sm outline-none',
        'data-slot': 'input-group-control',
        ...(id && { id })
      }
    },
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4] } }),
      Placeholder.configure({
        placeholder:
          dropdownValues.length > 0
            ? 'Pro Tip: Use / to embed an attribute'
            : 'Enter Value'
      }),
      TextAlign.configure({ types: ['paragraph'] }),
      SlashToken,
      slashCommandExtension
    ]
  });

  useEffect(() => {
    if (!id) return;

    const label = document.querySelector<HTMLLabelElement>(
      `label[for="${id}"]`
    );

    if (!label) return;

    const handleClick = () => {
      editor.commands.focus();
    };

    label.addEventListener('click', handleClick);

    return () => {
      label.removeEventListener('click', handleClick);
    };
  }, [editor, id]);

  useEffect(() => {
    if (!ref) return;

    ref.current = editor;
  }, [editor, ref]);

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isBlockquote: editor.isActive('blockquote'),
      isBold: editor.isActive('bold'),
      isBulletList: editor.isActive('bulletList'),
      isCode: editor.isActive('code'),
      isHeading1: editor.isActive('heading', { level: 1 }),
      isHeading2: editor.isActive('heading', { level: 2 }),
      isHeading3: editor.isActive('heading', { level: 3 }),
      isHeading4: editor.isActive('heading', { level: 4 }),
      isItalic: editor.isActive('italic'),
      isLink: editor.isActive('link'),
      isOrderedList: editor.isActive('orderedList'),
      isStrike: editor.isActive('strike'),
      isTextAlignCenter: editor.isActive({ textAlign: 'center' }),
      isTextAlignLeft: editor.isActive({ textAlign: 'left' }),
      isTextAlignRight: editor.isActive({ textAlign: 'right' })
    })
  });

  // This prevents triggering the keyboard shortcuts of other components while the rich text field is in focus.
  const handleInputGroupKeyDown: ComponentProps<
    typeof InputGroup
  >['onKeyDown'] = event => {
    event.stopPropagation();
  };

  return (
    <>
      <InputGroup
        className="h-auto flex-col items-stretch gap-2 py-1.5 shadow-xs"
        onKeyDown={handleInputGroupKeyDown}
      >
        <EditorContent editor={editor} />
        <div className="flex items-center gap-x-0.5 px-4 pb-2">
          <Select
            defaultValue="normal"
            onValueChange={value => {
              if (value === 'normal') {
                editor.chain().focus().setParagraph().run();
              } else {
                setTimeout(() => {
                  editor
                    .chain()
                    .focus()
                    .toggleHeading({
                      level: +value.split('-')[1] as 1 | 2 | 3 | 4
                    })
                    .run();
                }, 200);
              }
            }}
            value={
              editorState.isHeading1
                ? 'heading-1'
                : editorState.isHeading2
                  ? 'heading-2'
                  : editorState.isHeading3
                    ? 'heading-3'
                    : editorState.isHeading4
                      ? 'heading-4'
                      : 'normal'
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="heading-1">Heading 1</SelectItem>
                <SelectItem value="heading-2">Heading 2</SelectItem>
                <SelectItem value="heading-3">Heading 3</SelectItem>
                <SelectItem value="heading-4">Heading 4</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-x-0.5">
            <Tooltip key="left">
              <TooltipTrigger asChild>
                <Toggle
                  aria-label="Align left"
                  onClick={() => {
                    editor.chain().focus().setTextAlign('left').run();
                  }}
                  size="sm"
                  value="left"
                  variant={editorState.isTextAlignLeft ? 'outline' : 'default'}
                >
                  <AlignLeft />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>Align left</TooltipContent>
            </Tooltip>
            <Tooltip key="center">
              <TooltipTrigger asChild>
                <Toggle
                  aria-label="Align center"
                  onClick={() => {
                    editor.chain().focus().setTextAlign('center').run();
                  }}
                  size="sm"
                  value="center"
                  variant={
                    editorState.isTextAlignCenter ? 'outline' : 'default'
                  }
                >
                  <AlignCenter />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>Align Center</TooltipContent>
            </Tooltip>
            <Tooltip key="right">
              <TooltipTrigger asChild>
                <Toggle
                  aria-label="Align right"
                  onClick={() => {
                    editor.chain().focus().setTextAlign('right').run();
                  }}
                  size="sm"
                  value="right"
                  variant={editorState.isTextAlignRight ? 'outline' : 'default'}
                >
                  <AlignRight />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>Align Right</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Toggle
                  aria-label="Bold"
                  onPressedChange={() => {
                    editor.chain().focus().toggleBold().run();
                  }}
                  size="sm"
                  type="button"
                  variant={editorState.isBold ? 'outline' : 'default'}
                >
                  <BoldIcon />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>Bold</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Toggle
                  aria-label="Italic"
                  onClick={() => {
                    editor.chain().focus().toggleItalic().run();
                  }}
                  size="sm"
                  type="button"
                  variant={editorState.isItalic ? 'outline' : 'default'}
                >
                  <ItalicIcon />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>Italic</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Toggle
                  aria-label="Strikethrough"
                  onClick={() => {
                    editor.chain().focus().toggleStrike().run();
                  }}
                  pressed={editorState.isStrike}
                  size="sm"
                  type="button"
                  variant={editorState.isStrike ? 'outline' : 'default'}
                >
                  <Strikethrough />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>Strikethrough</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Toggle
                  aria-label="Bulleted list"
                  onClick={() => {
                    editor.chain().focus().toggleBulletList().run();
                  }}
                  size="sm"
                  type="button"
                  variant={editorState.isBulletList ? 'outline' : 'default'}
                >
                  <List />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>Bulleted List</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Toggle
                  aria-label="Ordered list"
                  onClick={() => {
                    editor.chain().focus().toggleOrderedList().run();
                  }}
                  size="sm"
                  type="button"
                  variant={editorState.isOrderedList ? 'outline' : 'default'}
                >
                  <ListOrdered />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>Ordered List</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Toggle
                  aria-label="Quote"
                  onClick={() => {
                    editor.chain().focus().toggleBlockquote().run();
                  }}
                  size="sm"
                  type="button"
                  variant={editorState.isBlockquote ? 'outline' : 'default'}
                >
                  <MessageSquareQuote />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>Quote</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Toggle
                  aria-label="Inline code"
                  onClick={() => {
                    editor.chain().focus().toggleCode().run();
                  }}
                  size="sm"
                  type="button"
                  variant={editorState.isCode ? 'outline' : 'default'}
                >
                  <CodeIcon />
                </Toggle>
              </TooltipTrigger>
              <TooltipContent>Inline code</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </InputGroup>

      <SlashCommandList
        anchorRef={virtualAnchorRef}
        command={handleSlashCommand}
        items={slashMenu.items}
        onOpenChange={handleSlashMenuOpenChange}
        open={slashMenu.open}
        ref={slashListRef}
      />
    </>
  );
};

export default RichTextField;
